/**
 * Image Cache Service
 *
 * Provides a thin wrapper around the Cache Storage API so show and presenter
 * artwork can be fetched once, stored with an immutable cache policy, and then
 * re-used across components without triggering additional network requests.
 *
 * The filenames generated by the CMS are unique and content-addressable, so
 * we can safely cache them for a long period both in the browser and through
 * the CDN layer.
 */

import { debugLog, debugWarn } from '../utils/debugLog';

const IMAGE_CACHE_NAME = 'eruption-show-images-v1';
const MAX_CACHE_ENTRIES = 120;
const CACHE_CONTROL_HEADER = 'public, max-age=31536000, immutable';

type ImageSizeKey = 'small' | 'medium' | 'large';

interface ImageCacheMetrics {
  hits: number;
  misses: number;
  prefetchRequests: number;
  prefetchCached: number;
  prefetchStored: number;
  evictions: number;
  entries: number;
  approxBytes: number;
  lastTrimCount: number;
  lastError: string | null;
  lastUpdated: number;
  lastCleared: number | null;
}

interface ImageLoadBucket {
  total: number;
  hits: number;
  misses: number;
  totalLoadTime: number;
  mobileLoads: number;
  desktopLoads: number;
}

interface ImageLoadMetrics {
  total: number;
  hits: number;
  misses: number;
  totalLoadTime: number;
  mobileLoads: number;
  desktopLoads: number;
  perSize: Record<ImageSizeKey, ImageLoadBucket>;
}

export interface ImageCacheMetricsSnapshot extends ImageCacheMetrics {
  loads: {
    total: number;
    hits: number;
    misses: number;
    averageLoadTime: number;
    mobileLoads: number;
    desktopLoads: number;
    perSize: Record<ImageSizeKey, {
      total: number;
      hits: number;
      misses: number;
      averageLoadTime: number;
      mobileLoads: number;
      desktopLoads: number;
    }>;
  };
}

const metrics: ImageCacheMetrics = {
  hits: 0,
  misses: 0,
  prefetchRequests: 0,
  prefetchCached: 0,
  prefetchStored: 0,
  evictions: 0,
  entries: 0,
  approxBytes: 0,
  lastTrimCount: 0,
  lastError: null,
  lastUpdated: Date.now(),
  lastCleared: null
};

const createLoadBucket = (): ImageLoadBucket => ({
  total: 0,
  hits: 0,
  misses: 0,
  totalLoadTime: 0,
  mobileLoads: 0,
  desktopLoads: 0
});

const loadMetrics: ImageLoadMetrics = {
  total: 0,
  hits: 0,
  misses: 0,
  totalLoadTime: 0,
  mobileLoads: 0,
  desktopLoads: 0,
  perSize: {
    small: createLoadBucket(),
    medium: createLoadBucket(),
    large: createLoadBucket()
  }
};

const updateMetrics = (partial: Partial<ImageCacheMetrics>) => {
  Object.assign(metrics, partial, { lastUpdated: Date.now() });
};

const updateEntriesFromCache = async (cache: Cache) => {
  try {
    const keys = await cache.keys();
    updateMetrics({ entries: keys.length });
  } catch (error) {
    updateMetrics({ lastError: `cache.keys_failed:${(error as Error)?.message || 'unknown'}` });
  }
};

const recordLoadToBucket = (bucket: ImageLoadBucket, cacheHit: boolean, isMobile: boolean, loadTime: number) => {
  bucket.total += 1;
  bucket.totalLoadTime += loadTime;
  if (cacheHit) {
    bucket.hits += 1;
  } else {
    bucket.misses += 1;
  }

  if (isMobile) {
    bucket.mobileLoads += 1;
  } else {
    bucket.desktopLoads += 1;
  }
};

export const recordImageLoadMetric = (
  metric: {
    cacheHit: boolean;
    size: ImageSizeKey;
    isMobile: boolean;
    loadTime: number;
  }
) => {
  loadMetrics.total += 1;
  loadMetrics.totalLoadTime += metric.loadTime;

  if (metric.cacheHit) {
    loadMetrics.hits += 1;
  } else {
    loadMetrics.misses += 1;
  }

  if (metric.isMobile) {
    loadMetrics.mobileLoads += 1;
  } else {
    loadMetrics.desktopLoads += 1;
  }

  const bucket = loadMetrics.perSize[metric.size] ?? loadMetrics.perSize.small;
  recordLoadToBucket(bucket, metric.cacheHit, metric.isMobile, metric.loadTime);

  updateMetrics({});
};

interface CacheableResponse {
  url: string;
  cacheHit: boolean;
  objectUrl?: string;
}

const isCacheStorageAvailable = (): boolean =>
  typeof window !== 'undefined' &&
  typeof caches !== 'undefined' &&
  typeof caches.open === 'function';

const supportsObjectUrls = (): boolean =>
  typeof URL !== 'undefined' &&
  typeof URL.createObjectURL === 'function' &&
  typeof URL.revokeObjectURL === 'function';

const createObjectUrl = (blob: Blob): string | null => {
  if (!supportsObjectUrls()) {
    return null;
  }

  try {
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const trimCache = async (cache: Cache) => {
  const entries = await cache.keys();
  if (entries.length <= MAX_CACHE_ENTRIES) {
    return 0;
  }

  const overflow = entries.length - MAX_CACHE_ENTRIES;
  for (let index = 0; index < overflow; index += 1) {
    await cache.delete(entries[index]);
  }
  updateMetrics({
    evictions: metrics.evictions + overflow,
    lastTrimCount: overflow
  });
  await updateEntriesFromCache(cache);
  return overflow;
};

const isSameOrigin = (imageUrl: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const resolved = new URL(imageUrl, window.location.href);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
};

const isCrossOriginCachingEnabled = (): boolean =>
  typeof process !== 'undefined' &&
  Boolean(
    process.env.REACT_APP_IMAGE_CACHE_ALLOW_CROSS_ORIGIN &&
      ['1', 'true', 'on', 'enabled', 'yes'].includes(
        String(process.env.REACT_APP_IMAGE_CACHE_ALLOW_CROSS_ORIGIN).trim().toLowerCase()
      )
  );

const isCacheableImageUrl = (imageUrl: string): boolean => {
  if (!imageUrl) {
    return false;
  }

  if (isSameOrigin(imageUrl)) {
    return true;
  }

  return isCrossOriginCachingEnabled();
};

const fetchImage = (imageUrl: string): Promise<Response> =>
  fetch(imageUrl, {
    mode: 'cors',
    cache: 'force-cache',
    credentials: 'omit',
    headers: {
      Accept: 'image/avif,image/webp,image/apng,image/*,*/*;q=0.8',
      'Cache-Control': CACHE_CONTROL_HEADER
    }
  });

export const getCachedImage = async (imageUrl: string): Promise<CacheableResponse> => {
  if (!imageUrl) {
    return { url: imageUrl, cacheHit: false };
  }

  if (!isCacheStorageAvailable() || !isCacheableImageUrl(imageUrl)) {
    return { url: imageUrl, cacheHit: false };
  }

  try {
    const cache = await caches.open(IMAGE_CACHE_NAME);
    const cachedResponse = await cache.match(imageUrl);

    if (cachedResponse) {
      await updateEntriesFromCache(cache);
      updateMetrics({ hits: metrics.hits + 1 });
      const blob = await cachedResponse.blob();
      const objectUrl = createObjectUrl(blob);

      if (objectUrl) {
        debugLog('🗃️ Served image from cache', imageUrl);
        return { url: objectUrl, cacheHit: true, objectUrl };
      }

      return { url: imageUrl, cacheHit: true };
    }

    updateMetrics({ misses: metrics.misses + 1 });
    const response = await fetchImage(imageUrl);

    if (!response.ok) {
      debugWarn('⚠️ Image fetch skipped caching due to non-200 response', {
        imageUrl,
        status: response.status
      });
      updateMetrics({ lastError: `fetch_failed:${response.status}` });
      return { url: imageUrl, cacheHit: false };
    }

    try {
      const cacheCopy = response.clone();
      await cache.put(imageUrl, cacheCopy);
      const trimmed = await trimCache(cache);
      if (!trimmed) {
        await updateEntriesFromCache(cache);
      }
      debugLog('⬇️ Cached new image response', imageUrl);
    } catch (error) {
      debugWarn('⚠️ Unable to store image in cache', { imageUrl, error });
      updateMetrics({ lastError: `cache_put_failed:${(error as Error)?.message || 'unknown'}` });
    }

    const blob = await response.blob();
    const objectUrl = createObjectUrl(blob);
    if (blob && typeof blob.size === 'number') {
      updateMetrics({ approxBytes: metrics.approxBytes + blob.size });
    }

    if (objectUrl) {
      return { url: objectUrl, cacheHit: false, objectUrl };
    }

    return { url: imageUrl, cacheHit: false };
  } catch (error) {
    debugWarn('⚠️ Image cache lookup failed', { imageUrl, error });
    updateMetrics({ misses: metrics.misses + 1, lastError: `cache_lookup_failed:${(error as Error)?.message || 'unknown'}` });
    return { url: imageUrl, cacheHit: false };
  }
};

export const prefetchImages = async (imageUrls: string[]): Promise<void> => {
  if (!imageUrls.length || !isCacheStorageAvailable()) {
    return;
  }

  try {
    const cache = await caches.open(IMAGE_CACHE_NAME);

    await Promise.all(
      imageUrls.map(async (imageUrl) => {
        if (!imageUrl || !isCacheableImageUrl(imageUrl)) {
          return;
        }

        updateMetrics({ prefetchRequests: metrics.prefetchRequests + 1 });
        const cached = await cache.match(imageUrl);
        if (cached) {
          updateMetrics({ prefetchCached: metrics.prefetchCached + 1 });
          return;
        }

        try {
          const response = await fetchImage(imageUrl);
          if (response.ok) {
            const cacheCopy = response.clone();
            await cache.put(imageUrl, cacheCopy);
            debugLog('🚀 Prefetched image into cache', imageUrl);
            const blob = await response.blob();
            if (typeof blob.size === 'number') {
              updateMetrics({
                prefetchStored: metrics.prefetchStored + 1,
                approxBytes: metrics.approxBytes + blob.size
              });
            } else {
              updateMetrics({ prefetchStored: metrics.prefetchStored + 1 });
            }
          }
        } catch (error) {
          debugWarn('⚠️ Failed to prefetch image', { imageUrl, error });
          updateMetrics({ lastError: `prefetch_failed:${(error as Error)?.message || 'unknown'}` });
        }
      })
    );

    const trimmed = await trimCache(cache);
    if (!trimmed) {
      await updateEntriesFromCache(cache);
    }
  } catch (error) {
    debugWarn('⚠️ Prefetch operation aborted', { error });
    updateMetrics({ lastError: `prefetch_aborted:${(error as Error)?.message || 'unknown'}` });
  }
};

export const releaseObjectUrl = (objectUrl?: string) => {
  if (!objectUrl || !supportsObjectUrls()) {
    return;
  }

  URL.revokeObjectURL(objectUrl);
};

export const clearImageCache = async () => {
  if (!isCacheStorageAvailable()) {
    return;
  }

  await caches.delete(IMAGE_CACHE_NAME);
  updateMetrics({
    entries: 0,
    approxBytes: 0,
    lastCleared: Date.now()
  });
};

export const isImageCacheAvailable = (): boolean => isCacheStorageAvailable();

const buildLoadSnapshot = () => {
  const perSize = loadMetrics.perSize;
  const toSnapshot = (bucket: ImageLoadBucket) => ({
    total: bucket.total,
    hits: bucket.hits,
    misses: bucket.misses,
    averageLoadTime: bucket.total ? bucket.totalLoadTime / bucket.total : 0,
    mobileLoads: bucket.mobileLoads,
    desktopLoads: bucket.desktopLoads
  });

  return {
    total: loadMetrics.total,
    hits: loadMetrics.hits,
    misses: loadMetrics.misses,
    averageLoadTime: loadMetrics.total ? loadMetrics.totalLoadTime / loadMetrics.total : 0,
    mobileLoads: loadMetrics.mobileLoads,
    desktopLoads: loadMetrics.desktopLoads,
    perSize: {
      small: toSnapshot(perSize.small),
      medium: toSnapshot(perSize.medium),
      large: toSnapshot(perSize.large)
    }
  };
};

export const getImageCacheMetrics = (): ImageCacheMetricsSnapshot => ({
  ...metrics,
  loads: buildLoadSnapshot()
});

const resetLoadBucket = (bucket: ImageLoadBucket) => {
  bucket.total = 0;
  bucket.hits = 0;
  bucket.misses = 0;
  bucket.totalLoadTime = 0;
  bucket.mobileLoads = 0;
  bucket.desktopLoads = 0;
};

export const __resetImageCacheMetrics = () => {
  updateMetrics({
    hits: 0,
    misses: 0,
    prefetchRequests: 0,
    prefetchCached: 0,
    prefetchStored: 0,
    evictions: 0,
    entries: 0,
    approxBytes: 0,
    lastTrimCount: 0,
    lastError: null,
    lastCleared: null
  });

  loadMetrics.total = 0;
  loadMetrics.hits = 0;
  loadMetrics.misses = 0;
  loadMetrics.totalLoadTime = 0;
  loadMetrics.mobileLoads = 0;
  loadMetrics.desktopLoads = 0;
  resetLoadBucket(loadMetrics.perSize.small);
  resetLoadBucket(loadMetrics.perSize.medium);
  resetLoadBucket(loadMetrics.perSize.large);
};
